import { useMemo } from "react";
import { BotonExportarExcel, PageHeader, PaginadorTabla, Stack } from "@/components/ui";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";
import {
  listarHistorialAutorizaciones,
  type HistorialAutorizacionItem,
} from "../services/historial-service";
import {
  HISTORIAL_PAGE_SIZE,
  useHistorialAutorizaciones,
} from "../hooks/use-historial-autorizaciones";
import { HistorialResumenCard } from "./HistorialResumenCard";
import { HistorialDesktopTable, type HistorialFiltros } from "./HistorialDesktopTable";
import { HistorialMobileList } from "./HistorialMobileList";
import { HistorialVacioCard } from "./HistorialVacioCard";
import { ENCABEZADO_HISTORIAL_AUTORIZACIONES, HistorialAutorizacionesSkeleton } from "./HistorialAutorizacionesSkeleton";

const COLUMNAS_EXCEL: ColumnaExcel<HistorialAutorizacionItem>[] = [
  { header: "Folio", value: (h) => h.folio },
  { header: "Nivel autorizado", value: (h) => `${h.nivelAutorizado}/${h.cantidadNiveles}` },
  { header: "Autorizado el", value: (h) => h.autorizadoAt },
  { header: "Estado actual", value: (h) => h.estadoActualSolicitud },
  { header: "Organización", value: (h) => h.organizacionNombre },
  { header: "Tipo", value: (h) => h.tipo },
  { header: "Subtipo", value: (h) => h.subtipo },
  { header: "Área", value: (h) => h.areaNombre },
  { header: "Proveedor", value: (h) => h.proveedorRazonSocial },
  { header: "Núm. proveedor", value: (h) => h.proveedorNumero },
  { header: "RFC emisor", value: (h) => h.rfcEmisor },
  { header: "Concepto", value: (h) => h.descripcionPrimerConcepto },
  { header: "Total CFDI", value: (h) => h.totalCfdi },
  { header: "Moneda", value: (h) => h.monedaCfdi },
  { header: "Fecha de pago", value: (h) => h.fechaPago },
];

/** Filtros con al menos un valor no vacío → objeto listo para query params. */
function filtrosNoVacios(filtros: HistorialFiltros): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filtros)) {
    if (v && v.trim().length > 0) out[k] = v.trim();
  }
  return out;
}

/**
 * Historial de autorizaciones · todas las firmas que el usuario actual ha
 * dado, ordenadas por fecha descendente. Aplica a Autorizador y a Mesa
 * de Control (que también autoriza Nivel 3).
 *
 * Filtros por columna (fila de búsqueda bajo los encabezados) y PAGINADO,
 * ambos resueltos en el SERVIDOR (query params, con debounce) · mismo
 * patrón que la Bandeja de autorización. Sin polling · es un histórico.
 */
export function HistorialAutorizacionesPage() {
  // Estado de tabla server-side (F1 · página + filtros debounced + reset a pág. 1).
  const { filtros, setFiltros, filtrosDebounced, page, setPage, pageSize } =
    useServerPagination<HistorialFiltros>({}, { pageSize: HISTORIAL_PAGE_SIZE });

  const serverParams = useMemo(
    () => ({
      ...filtrosNoVacios(filtrosDebounced),
      page,
      pageSize,
    }),
    [filtrosDebounced, page, pageSize],
  );

  const { data, isPending, isPlaceholderData } = useHistorialAutorizaciones(serverParams);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats;

  const hayFiltros = Object.keys(filtrosNoVacios(filtrosDebounced)).length > 0;

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodas = async (): Promise<HistorialAutorizacionItem[]> => {
    const params = filtrosNoVacios(filtrosDebounced);
    const todas: HistorialAutorizacionItem[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarHistorialAutorizaciones({ ...params, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  // Primera carga sin caché · el mismo skeleton que el pendingComponent de la ruta.
  if (isPending) return <HistorialAutorizacionesSkeleton />;

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title={ENCABEZADO_HISTORIAL_AUTORIZACIONES.titulo}
        description={ENCABEZADO_HISTORIAL_AUTORIZACIONES.descripcion}
        breadcrumbs={[...ENCABEZADO_HISTORIAL_AUTORIZACIONES.breadcrumbs]}
        actions={
          <BotonExportarExcel
            archivo="historial-autorizaciones"
            hoja="Historial"
            columnas={COLUMNAS_EXCEL}
            obtenerFilas={obtenerTodas}
          />
        }
      />
      <Stack gap="md">
        <HistorialResumenCard total={total} stats={stats} />
        {total === 0 && !hayFiltros ? (
          <HistorialVacioCard />
        ) : (
          <>
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={HISTORIAL_PAGE_SIZE}
              onPageChange={setPage}
              variante="superior"
            />
            <HistorialDesktopTable
              items={items}
              filtros={filtros}
              buscando={isPlaceholderData}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
            />
            <HistorialMobileList items={items} />
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={HISTORIAL_PAGE_SIZE}
              onPageChange={setPage}
              sustantivo="firmas"
            />
          </>
        )}
      </Stack>
    </div>
  );
}
